import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { BRAND, NICKNAME_MAX, validateNickname } from '@tarot/shared';
import { updateNickname } from '../../lib/api';
import { useMe } from './useMe';
import { StarMark } from '../../brand/BrandLogo';

/** 첫 가입 직후 닉네임 설정 — OAuth 콜백이 신규 유저만 /welcome 으로 보낸다 */
export function WelcomePage() {
  const { me, isLoading } = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // 자동 생성된 기본 닉네임을 입력창에 미리 채움
  useEffect(() => {
    if (me) setValue(me.nickname);
  }, [me]);

  // 비로그인 상태로 직접 접근하면 홈으로
  useEffect(() => {
    if (!isLoading && !me) navigate('/', { replace: true });
  }, [isLoading, me, navigate]);

  if (isLoading || !me) return <p className="py-16 text-center text-moon-dim">불러오는 중…</p>;

  const start = async () => {
    const check = validateNickname(value);
    if (!check.ok) return setError(check.reason);
    setBusy(true);
    setError(null);
    try {
      if (check.value !== me.nickname) {
        await updateNickname(check.value);
        await qc.invalidateQueries({ queryKey: ['me'] });
      }
      navigate('/', { replace: true });
    } catch {
      setError('닉네임을 저장하지 못했어요');
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6 text-center">
      <StarMark className="h-14 w-14 text-gold" />
      <div className="flex flex-col gap-1.5">
        <h2 className="text-2xl font-bold">환영해요!</h2>
        <p className="text-sm leading-relaxed text-moon-dim">
          {BRAND.nameKo}에서 사용할 닉네임을 정해 주세요.
          <br />
          커플·우정 방에서 함께 보는 사람들에게 보여요.
        </p>
      </div>

      <div className="flex w-full max-w-xs flex-col gap-2">
        <input
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          maxLength={NICKNAME_MAX}
          autoFocus
          placeholder="닉네임"
          className="rounded-2xl border border-gold/40 bg-night-2 px-4 py-3 text-center font-semibold focus:border-gold focus:outline-none"
          onKeyDown={(e) => e.key === 'Enter' && start()}
        />
        <p className="text-xs text-moon-dim/70">
          최대 {NICKNAME_MAX}자 · 나중에 [내 정보]에서 바꿀 수 있어요
        </p>
        {error && <p className="text-sm text-red-300">⚠️ {error}</p>}
      </div>

      <button
        onClick={start}
        disabled={busy}
        className="flex h-12 w-full max-w-xs items-center justify-center rounded-2xl bg-gold px-5 text-sm font-semibold text-night transition hover:bg-gold-deep disabled:opacity-50"
      >
        {busy ? '저장 중…' : '이 닉네임으로 시작하기'}
      </button>
    </div>
  );
}
